import React, { useState, useMemo } from 'react';
import {
  Box,
  Chip,
  ClickAwayListener,
  Typography,
  Paper,
  Checkbox,
  Tooltip,
  CircularProgress,
  Button,
} from '@mui/material';
import SnoozeIcon from '@mui/icons-material/Snooze';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { InboxItem } from '../types';
import { AutocompleteAircraft, useAircraftData } from '../../Settings/HangarSettings/AutocompleteAircraft';
import { findBestMatch } from '../../Settings/HangarSettings/aircraftFilterUtils';
import { SnoozePopover } from './SnoozePopover';

export interface QuarantineCardProps {
  item: InboxItem;
  isSelected?: boolean;
  onToggleSelect?: () => void;
  showCheckbox?: boolean;
  onResolve: (aircraftId: string, aircraftName: string) => Promise<void>;
  onSnooze: (until: Date) => Promise<void>;
  isResolving?: boolean;
}

/**
 * Card for quarantine items (unknown aircraft)
 * Fixed-width columns for easy scanning
 */
function QuarantineCardComponent({
  item,
  isSelected = false,
  onToggleSelect,
  showCheckbox = false,
  onResolve,
  onSnooze,
  isResolving = false,
}: QuarantineCardProps) {
  const { t } = useTranslation();
  const [snoozeAnchor, setSnoozeAnchor] = useState<HTMLElement | null>(null);
  const [isCompleting, setIsCompleting] = useState(false);
  const [selectedAircraft, setSelectedAircraft] = useState<{ id: string; make: string; model: string } | null>(null);
  const [showSearch, setShowSearch] = useState(false);

  // Get aircraft list from shared cache (no extra API call)
  const aircraftData = useAircraftData();

  // Find the best matching aircraft for the type hint
  const bestMatch = useMemo(() => {
    if (!item.aircraft_type_hint || !aircraftData.value) {
      return null;
    }
    const result = findBestMatch(item.aircraft_type_hint, aircraftData.value);
    return result.isExactMatch ? result.aircraft : null;
  }, [item.aircraft_type_hint, aircraftData.value]);

  const handleAircraftSelect = async (aircraft: any) => {
    if (!aircraft) {
      setSelectedAircraft(null);
      return;
    }

    setSelectedAircraft(aircraft);

    try {
      setIsCompleting(true);
      await onResolve(aircraft.id, `${aircraft.make} ${aircraft.model}`);
    } catch (err) {
      setIsCompleting(false);
      setSelectedAircraft(null);
    }
  };

  const handleSnooze = async (until: Date) => {
    setSnoozeAnchor(null);
    await onSnooze(until);
  };

  return (
    <Paper
      elevation={0}
      data-testid={`quarantine-card-${item.item_id}`}
      data-item-type="quarantine"
      data-tail-number={item.tail_number}
      sx={{
        p: 1.5,
        px: 2,
        borderRadius: 1,
        border: '1px solid',
        borderColor: isSelected ? '#635bff' : 'rgba(0, 0, 0, 0.08)',
        borderLeft: '3px solid',
        borderLeftColor: isSelected ? '#635bff' : '#ef4444',
        bgcolor: isCompleting
          ? 'rgba(16, 185, 129, 0.04)'
          : isSelected
          ? 'rgba(99, 91, 255, 0.04)'
          : '#fff',
        transition: 'all 150ms ease-out',
        opacity: isCompleting ? 0.7 : 1,
        '&:hover': {
          bgcolor: isSelected ? 'rgba(99, 91, 255, 0.06)' : 'rgba(0, 0, 0, 0.02)',
          borderColor: isSelected ? '#635bff' : 'rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        {/* Checkbox - fixed width */}
        {showCheckbox && onToggleSelect && (
          <Checkbox
            checked={isSelected}
            onChange={onToggleSelect}
            size="small"
            sx={{ p: 0, width: 24 }}
          />
        )}

        {/* Tail Number - fixed width */}
        <Box sx={{ width: 100, flexShrink: 0 }}>
          <Chip
            label={item.tail_number}
            size="small"
            color="error"
            variant="outlined"
            sx={{ fontWeight: 700, fontSize: 13 }}
          />
        </Box>

        {/* FlightBridge Type Hint */}
        <Box sx={{ minWidth: 120, maxWidth: 200 }}>
          {item.aircraft_type_hint ? (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                fontSize: 13,
                color: '#635bff',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={item.aircraft_type_hint}
            >
              {item.aircraft_type_hint}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12, fontStyle: 'italic' }}>
              {t('screens.operationsInbox.quarantineCard.noTypeHint')}
            </Typography>
          )}
        </Box>

        {/* Operator Name - grows to fill space */}
        <Box sx={{ flex: 1, minWidth: 100 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: 12,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={item.operator_name || ''}
          >
            {item.operator_name || '—'}
          </Typography>
        </Box>

        {/* Completion indicator */}
        {isCompleting && (
          <CheckCircleIcon sx={{ color: '#10b981', fontSize: 24, ml: 'auto' }} />
        )}

        {/* Aircraft Matching - Smart suggestion + search */}
        {!isCompleting && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            {/* Show "Match to [type]" button if we have a type hint and search isn't open */}
            {!showSearch && item.aircraft_type_hint && (
              <Tooltip title={t('screens.operationsInbox.quarantineCard.tooltips.searchForType', { type: item.aircraft_type_hint })} arrow>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<SearchIcon fontSize="small" />}
                  onClick={() => setShowSearch(true)}
                  disabled={isResolving}
                  sx={{
                    minWidth: 'auto',
                    px: 1.5,
                    py: 0.25,
                    fontSize: 11,
                    textTransform: 'none',
                    borderColor: '#635bff',
                    color: '#635bff',
                    '&:hover': {
                      bgcolor: '#635bff',
                      color: 'white',
                      borderColor: '#635bff',
                    },
                  }}
                >
                  {t('screens.operationsInbox.quarantineCard.buttons.matchTo', { type: item.aircraft_type_hint.split(' ').slice(0, 2).join(' ') })}
                </Button>
              </Tooltip>
            )}

            {/* Show search button if no type hint and search isn't open */}
            {!showSearch && !item.aircraft_type_hint && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<SearchIcon fontSize="small" />}
                onClick={() => setShowSearch(true)}
                disabled={isResolving}
                sx={{
                  minWidth: 'auto',
                  px: 1.5,
                  py: 0.25,
                  fontSize: 11,
                  textTransform: 'none',
                  borderColor: 'divider',
                  color: 'text.primary',
                  '&:hover': {
                    bgcolor: 'primary.main',
                    color: 'white',
                    borderColor: 'primary.main',
                  },
                }}
              >
                {t('screens.operationsInbox.quarantineCard.buttons.findAircraft')}
              </Button>
            )}

            {/* Aircraft search dropdown */}
            {showSearch && (
              <ClickAwayListener onClickAway={() => setShowSearch(false)}>
                <Box sx={{ width: 220, position: 'relative' }}>
                  <AutocompleteAircraft
                    label={t('screens.operationsInbox.quarantineCard.labels.searchAircraft')}
                    defaultSelected={selectedAircraft as any}
                    onSelect={handleAircraftSelect}
                    initialSearchText={item.aircraft_type_hint || ''}
                    autoOpen={true}
                    defaultHighlightedMatch={bestMatch || undefined}
                    onDismiss={() => setShowSearch(false)}
                    disablePortal={true}
                  />
                </Box>
              </ClickAwayListener>
            )}
          </Box>
        )}

        {/* Snooze Button */}
        {!isCompleting && (
          <>
            <Button
              size="small"
              variant="text"
              startIcon={<SnoozeIcon fontSize="small" />}
              onClick={(e) => setSnoozeAnchor(e.currentTarget)}
              disabled={isResolving}
              sx={{
                minWidth: 'auto',
                px: 1,
                py: 0.25,
                fontSize: 11,
                textTransform: 'none',
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              {t('screens.operationsInbox.quarantineCard.buttons.later')}
            </Button>
            <SnoozePopover
              anchorEl={snoozeAnchor}
              onClose={() => setSnoozeAnchor(null)}
              onSnooze={handleSnooze}
            />
          </>
        )}

        {/* Loading indicator */}
        {isResolving && !isCompleting && (
          <CircularProgress size={20} />
        )}
      </Box>
    </Paper>
  );
}

export const QuarantineCard = React.memo(QuarantineCardComponent);
